<i18n>
{
  "en": {
    "title": "Environmental Strategy & Sustainability 2017-2018",
    "description": "Sustainability and environmental responsibility are about more than risk mitigation. They are an engine for innovation and growth. Good environmental strategy is a good business strategy. And good environmental performance makes for good business performance.",
    "introduction": {
      "section": "Introduction",
      "title": "Environmental Strategy",
      "strategy": {
        "vid": {
          "title": "Sustainability as Business",
          "description": "Xavier Houot, <br> SVP Global Safety, Environment, Real Estate, Schneider Electric"
        },
        "innovationEngine": {
          "title": "An engine for innovation",
          "para": "Decades ago, sustainability and environmental concerns emerged as scientific studies began to uncover the harm being done to the planet by development and industry. Some corporations reacted to this trend by trying to demonstrate how they were working to use fewer resources or to offset their carbon footprint. Sustainability and environmental responsibility were seen as risk mitigation — as a burden for corporations to bear lest their reputation be damaged in the public eye. Indeed, some organizations still operate in this manner, with sustainability and environmental responsibility viewed as “nice to haves” instead of the what they truly are: an engine for innovation.",
          "paraB": "The reality is, good environmental strategy is good business strategy. And good environmental performance makes for good business performance."
        },
        "businessGrowth": {
          "title": "Environmental responsibility: an opportunity for business growth",
          "para": "The myth has now evaporated &mdash; business growth and environmental performance are not at odds. Quite the opposite &mdash; environmental excellence helps drive business growth. Leading companies have recognized that environmental performance is a boon to the bottom line. Renewable energy is now cheaper in many areas than traditional brown power.<sup>1</sup> The economy of the future is low carbon, and companies that prepare for that future will quickly gain a competitive advantage and be able to invent faster and address new customer needs earlier.",
          "paraB": "For businesses, seeking environmental excellence makes good business sense, helping to ensure long-term market viability, competitiveness, and resiliency.",
          "itemA": "<strong>Increased profitability</strong> — Evidence shows that companies demonstrating leadership on climate action are more profitable. A 2014 CDP study of 500 S&P industry leaders found that organizations actively managing and planning for climate change secure an 18 percent higher return on equity or investment versus non-committed peers.",
          "itemB": "<strong>Improved efficiency</strong> — Making a clear and bold commitment to reducing carbon emissions and decoupling growth from resource intake often give rise to greater efficiency as companies find ways to improve processes, reduce raw material inputs, and lower energy consumption",
          "itemC": "<strong>More innovation</strong> — Environmental responsibility acts as a transformative lever for change, helping to spur innovation in low-carbon products, technologies, and services.",
          "itemD": "<strong>Greater access to capital</strong> — Investors and financial institutions continue to demand more disclosure on environmental performance and want to help their own customers reduce their carbon footprints. Science-based targets provide greater visibility and assurance of what a company is trying to achieve."
        },
        "barometer": {
          "title": "2015 – 2017 Planet <span class='t-nowrap'>& Society</span> barometer achievements",
          "one": {
            "title": "Climate & Development (Sustainability offers)",
            "stat": "80.1%",
            "body": "of product revenue with Green Premium ecoLabel&trade;"
          },
          "two": {
            "title": "Circular economy",
            "stat": "130",
            "body": "Toward zero waste to landfill for 100 industrial sites"
          },
          "cta": "See the full results"
        },
        "strategyPillars": {
          "title": "Our six pillars of environmental strategy",
          "para": "At Schneider Electric, our environmental strategy extends our vision for sustainability into six principles that guide our operations. There is a difference between an environmental strategy that focuses only on a company’s internal operations and one that reaches beyond to transform an entire supply chain from raw materials to customer delivery. We believe the latter, more difficult path is both the right way to operate and the only way to truly co-create a sustainable future."
        },
        "table1": {
          "row1": {
            "cell1": {
              "title": "Deploying low-CO<sub>2</sub> and resource-efficient strategies for planet-compatible growth path"
            },
            "cell2": {
              "p1": "We constantly strive to lower our CO<sub>2</sub> impact in every part of our organization and work to define and mitigate the CO<sub>2</sub> impact of all large projects."
            }
          },
          "row2": {
            "cell1": {
              "title": "Building an increasingly green",
              "link": "supply chain"
            },
            "cell2": {
              "p1": "We work toward energy efficiency, water efficiency, and the reduction of any environmentally harmful side effects of transportation and manufacturing."
            }
          },
          "row3": {
            "cell1": {
              "title": "Considering waste as worth"
            },
            "cell2": {
              "p1": "We’re working toward zero waste at our 1,150 facilities worldwide. This year, we are proud to have had 130 plants receive the “Toward Zero Waste to Landfill” designation."
            }
          },
          "row4": {
            "cell1": {
              "title": "Promoting green attributes and value-adds"
            },
            "cell2": {
              "p1": "Through initiatives like our Green Premium ecoLabel, we promote awareness of the environmental benefits of our products and how those benefits bring increased value."
            }
          },
          "row5": {
            "cell1": {
              "title": "Implementing a circular economy"
            },
            "cell2": {
              "p1": "We work to keep our resources in use for as long as possible, extract the maximum value, and then recover and regenerate at the end of product lifetime. We offer services that prolong the lifetime of our products, helping our customers enjoy energy management and automation services while using fewer resources."
            }
          },
          "row6": {
            "cell1": {
              "title": "Strengthening our environmental governance"
            },
            "cell2": {
              "p1": "With more than 1,000 independent assessments, plus hundreds of field visits and audits, we review environmental risks in our",
              "p2": "and how we comply with changing regulations or report to a variety of external stakeholders and analysts.",
              "link": "supply chain"
            }
          }
        },
        "statement1": "Our environmental strategy is deployed throughout the company via a network of over 400 environmental leaders in charge of eco-designing new offers, greening our supply chain, inventing circular business models, and quantifying environmental benefits for customers. We always keep three things in mind: customer expectations, our Planet-Climate-Life preservation ambitions, and our business strategy.",
        "targetsInitiative": {
          "title": "The Science Based Targets initiative",
          "paraA": "Any truly responsible environmental strategy must be quantified, specific, and evidence-based. A collaborative effort between CDP, the U.N. Global Compact, World Resource Institute, and World Wildlife Fund, the Science Based Targets initiative helps corporations establish accurate and verified targets backed by climate science.",
          "paraB1": "The time has come for ",
          "paraBLink": "all organizations",
          "paraB2": " to move beyond the question, “What can we do to incrementally improve our carbon footprint?” and to answer:",
          "itemA": "Exactly how much greenhouse gas emission does our organization need to reduce in order to meet the targets of the Paris Climate Agreement?",
          "itemB": "How quickly does our organization need to accomplish that reduction?",
          "paraC": "Schneider Electric is among the more than 400 companies working with the Science Based Target initiative (SBTi). We’re currently at the second step of their four-step process — Commit, Develop, Submit, Announce — and look forward to sharing our targets publicly and doing our part to achieve the goals of the Paris Climate Agreement."
        },
        "moveTheDate": {
          "title": "#MoveTheDate",
          "para": "When it comes to environmental responsibility, incremental gains are no longer enough. We already consume 1.7 planets' worth of resources per year.<sup>2</sup> And we know that if we do nothing, we are bound to a 4 °C increase in global temperature by 2100.",
          "paraB": "In 2017, August 2 was Earth Overshoot Day. This means that in 2017, all of us who call Earth home used more from nature than our planet can renew in the whole year — and we did so in less than eight months. Overshoot happens through overfishing, overharvesting forests, and emitting more carbon dioxide into the atmosphere than forests can recycle into oxygen. We must #MoveTheDate. And we must not stop moving it until what we consume is in a responsible balance with what the planet can produce.",
          "paraC": "With this in mind, businesses must look at environmental strategy and performance in three ways:"
        },
        "table2": {
          "row1": {
            "cell1": {
              "title": "Climate"
            },
            "cell2": {
              "p1": "Is your business model and global supply chain <strong>+2 °C compatible</strong>?",
              "p2": "At Schneider, we actively quantify how \"More Schneider means a better climate\" by assessing our own CO<sub>2</sub> footprint and calculating the amount of CO<sub>2</sub> avoided through our customers’ use of our technologies."
            }
          },
          "row2": {
            "cell1": {
              "title": "Planet"
            },
            "cell2": {
              "p1": "Is your raw material consumption and circular take-back strategy <strong>One Planet compatible</strong>?",
              "p2": "At Schneider, we’ve partnered with Global Footprint Network to continually assess whether we are helping to #MoveTheDate each year, and where, and when, and how we can accelerate toward that goal."
            }
          },
          "row3": {
            "cell1": {
              "title": "Life"
            },
            "cell2": {
              "p1": "Are your supply chain practices and value propositions <strong>life-preservation compatible</strong>?",
              "p2": "At Schneider, we track the use of hazardous chemicals and substances yearly and seek to reduce or discontinue them entirely — in our plants, in our products, and in our packaging. Our hazardous waste and emissions have to be  strictly handled."
            }
          }
        },
        "references": {
          "r1": "<sup>1</sup> http://www.independent.co.uk/environment/solar-and-wind-power-cheaper-than-fossil-fuels-for-the-first-time-a7509251.html",
          "r2": "<sup>2</sup> According to the Global Footprint Network"
        }
      }
    }
  },
  "fr": {
    "title": "Environmental Strategy & Sustainability 2017-2018",
    "description": "Sustainability and environmental responsibility are about more than risk mitigation. They are an engine for innovation and growth. Good environmental strategy is a good business strategy. And good environmental performance makes for good business performance.",
    "introduction": {
      "section": "Introduction",
      "title": "Notre Stratégie Environnementale",
      "strategy": {
        "vid": {
          "title": "La responsabilité environnementale : une opportunité de croissance",
          "description": "Xavier Houot, <br>Directeur Sécurité, Environnement et Immobilier, Schneider Electric"
        },
        "innovationEngine": {
          "title": "Un moteur pour l’innovation",
          "para": "Les préoccupations liées au développement durable et à l’environnement ont émergé il y a plusieurs décennies lorsque des études scientifiques ont commencé à mettre en lumière les dangers du développement économique et de l’industrie pour la planète. Certaines entreprises réagirent en tentant de montrer les efforts entrepris pour utiliser moins de ressources ou pour compenser leur empreinte carbone. Le développement durable et la responsabilité environnementale étaient envisagés sous l’angle de la maîtrise des risques : pour les entreprises, il s'agissait d’un passage obligé pour protéger leur réputation aux yeux du grand public. De fait, certaines organisations continuent d’agir de cette manière et de considérer le développement durable et la responsabilité environnementale comme un ornement, au lieu de les voir pour ce qu’ils sont vraiment : un moteur pour l’innovation.",
          "paraB": "Or, si la stratégie environnementale est efficace, la stratégie d’entreprise l’est aussi, car une bonne performance environnementale porte ses fruits sur le plan économique."
        },
        "businessGrowth": {
          "title": "La responsabilité environnementale : une opportunité pour gérer de la croissance",
          "para": "Le mythe est maintenant dissipé : la croissance économique et la performance environnementale ne sont plus antinomiques. Bien au contraire, l'excellence environnementale stimule la croissance. Des entreprises de premier plan ont reconnu que la performance  environnementale était un véritable atout pour le chiffre d’affaires. Dans de nombreux domaines, les énergies renouvelables sont désormais moins coûteuses que les énergies fossiles traditionnelles <sup>1</sup>. L'économie du futur sera une économie bas carbone, et les entreprises qui s’y préparent d’ores et déjà acquerront rapidement un avantage compétitif et seront capables d'inventer plus vite et de répondre aux besoins nouveaux de leurs clients plus tôt.",
          "paraB": "Pour une entreprise, les efforts en matière de performance environnementale se justifient économiquement dans la mesure où ils concourent à sa pérennité, à sa compétitivité et à sa résilience.",
          "itemA": "<strong>Rentabilité accrue</strong> — Les statistiques montrent que les entreprises à l’avant-garde en matière de lutte contre le changement climatique sont plus rentables. Selon une étude menée en 2014 par le CDP auprès d’entreprises du S&P 500, les entreprises qui déploient une véritable politique de gestion et de planification en matière de changement climatique enregistrent un retour sur investissement 18 % plus élevé que leurs concurrents.",
          "itemB": "<strong>Amélioration de l’efficacité</strong> — Les entreprises qui prennent un engagement ferme et clair à réduire leurs émissions de CO<sub>2</sub> engrangent des gains d’efficacité car elles mettent en place des moyens d'optimiser leurs processus, de réduire leur consommation de matières premières et de faire diminuer leur consommation énergétique.",
          "itemC": "<strong>Plus d’innovation</strong> — La responsabilité environnementale est un levier de transformation puisqu’elle stimule l’innovation dans le domaine des produits, des technologies et des services bas carbone.",
          "itemD": "<strong>Meilleur accès aux capitaux</strong> — Les investisseurs et les établissements financiers ne cessent d’exiger un reporting sur la performance environnementale toujours plus détaillé et de vouloir aider leurs clients à réduire leur empreinte carbone. Les <em>Science&ndash; based targets</em> donnent une meilleure visibilité et une assurance des objectifs poursuivis par l’entreprise à cet égard."
        },
        "barometer": {
          "title": "Résultats du baromètre Planète & Société 2015-2017",
          "one": {
            "title": "Climat et Développement (offres durables)",
            "stat": "80,1%",
            "body": "du chiffre d’affaires sur les produits réalisés avec Green Premium ecoLabel<sup>TM</sup>"
          },
          "two": {
            "title": "Economie circulaire",
            "stat": "130",
            "body": "Vers le zéro déchet mis en décharge pour 100 sites industriels"
          },
          "cta": "Voir l’ensemble des résultats.  "
        },
        "strategyPillars": {
          "title": "Les six piliers de notre stratégie environnementale",
          "para": "Chez Schneider Electric, nous déclinons dans notre stratégie environnementale notre vision du développement durable en six principes qui guident nos actions. Il faut bien faire la différence entre une stratégie environnementale uniquement axée sur l'activité interne de l’entreprise et une stratégie qui s'étend à toute la chaîne logistique, des matières premières à la fourniture client. Nous pensons que cette deuxième voie, certes plus difficile, est la bonne voie à suivre et la seule manière de réellement co-créer un avenir durable."
        },
        "table1": {
          "row1": {
            "cell1": {
              "title": "Définir une stratégie en termes de CO<sub>2</sub> et de ressources vers un parcours de croissance compatible avec le climat et notre planète"
            },
            "cell2": {
              "p1": "Nous nous efforçons sans cesse de diminuer notre empreinte carbone dans tous les secteurs de notre activité et tâchons de définir et de limiter l’impact CO₂ de tous nos grands projets."
            }
          },
          "row2": {
            "cell1": {
              "title": "Bâtir des opérations industrielles toujours plus verte",
              "link": ""
            },
            "cell2": {
              "p1": "L'efficacité énergétique, l'efficacité en terme d'utilisation de l'eau et la réduction de tous les effets secondaires nocifs pour l'environnnement dans les transports de la fabrication sont nos maîtres mots."
            }
          },
          "row3": {
            "cell1": {
              "title": "Envisager les déchets comme une ressource"
            },
            "cell2": {
              "p1": "Nous misons sur une démarche zéro déchet dans nos 1 150 sites à travers le monde. Cette année, nous sommes fiers d'annoncer que 130 de nos usines ont reçu la mention  « Vers le zéro déchet mis en décharge »."
            }
          },
          "row4": {
            "cell1": {
              "title": "Faire la promotion des atouts environnementaux et de la valeur ajoutée"
            },
            "cell2": {
              "p1": "Grâce à des initiatives comme notre ecoLabel Green Premium, nous mettons en avant les avantages de nos produits sur le plan environnemental et la valeur ajoutée qui en découle."
            }
          },
          "row5": {
            "cell1": {
              "title": "Mettre en place une économie circulaire"
            },
            "cell2": {
              "p1": "Nous nous efforçons d’optimiser la durée d’utilisation de nos ressources, d’en tirer le maximum, et de valoriser ou de réparer les produits et matériaux en fin de vie. Nous offrons des services pour prolonger la durée de vie de nos produits, et permettons à nos clients de bénéficier de services de gestion de l’énergie et d'automatisation tout en consommant moins de ressources."
            }
          },
          "row6": {
            "cell1": {
              "title": "Renforcer notre gouvernance environnementale"
            },
            "cell2": {
              "p1": "Avec plus de 1 000 évaluations indépendantes, mais aussi des centaines de visites sur le terrain et d’audits, nous mesurons les risques environnementaux de",
              "p2": ". Nous veillons également à respecter les règlements qui ne cessent d’évoluer et nous rendons des comptes à diverses parties prenantes ou analystes externes.",
              "link": "notre chaîne logistique"
            }
          }
        },
        "statement1": "Notre stratégie environnementale est déployée à travers le Groupe via un réseau de plus de 400 leaders environnement. Ils sont chargés d’assurer l’éco-conception de nos nouvelles offres, de créer des modèles économiques circulaires et de quantifier les bénéfices environnementaux pour nos clients. Nous gardons toujours à l'esprit trois choses : les attentes de nos clients, nos ambitions en matière de préservation de la planète, du climat, de la vie et notre stratégie d'entreprise.",
        "targetsInitiative": {
          "title": "L’initiative <em>Science&mdash; based targets</em>",
          "paraA": "Toute stratégie environnementale véritablement responsable doit être quantifiée, spécifique et reposer sur des éléments tangibles. Produit de la collaboration entre le CDP, le Pacte mondial des Nations unies, l’Institut des ressources mondiales et le Fonds mondial pour la nature (WWF), l’initiative <em>Science- based targets</em> aide les entreprises à établir des objectifs précis et vérifiés, alignés sur les connaissances scientifiques.",
          "paraB1": "Pour ",
          "paraBLink": "toutes les entreprises",
          "paraB2": ", il est temps de ne plus seulement se demander comment améliorer pas à pas leur empreinte carbone et de répondre aux questions suivantes : ",
          "itemA": "Quel volume d'émissions de gaz à effet de serre devons-nous éviter afin d'atteindre les objectifs fixés par l’accord de Paris sur le climat ?",
          "itemB": "À quelle vitesse  devons-nous progresser dans ce domaine ?",
          "paraC": "Schneider Electric fait partie d'une coalition de plus de 400 entreprises associées à l’initiative <em>Science Based Targets</em> (iSBT). Nous en sommes actuellement à la deuxième étape de leur processus en quatre temps &mdash; engagement, développement, soumission, annonce &mdash; puis  nous publierons nos objectifs et notre plan d'actions pour concrétiser les objectifs de l’accord de Paris sur le climat."
        },
        "moveTheDate": {
          "title": "#MoveTheDate",
          "para": "En matière de responsabilité environnementale, les gains progressifs ne suffisent plus. Chaque année, nous consommons déjà des ressources équivalentes à celles d’1,7 planète<sup>2</sup>. Et nous savons que si nous ne faisons rien, nous sommes condamnés à voir les températures mondiales augmenter de 4 °C d’ici 2100.",
          "paraB": "En 2017, le jour de dépassement de la Terre est tombé le 2 août. Cela signifie qu’en 2017, tous les habitants de la planète réunis ont consommé plus de ressources naturelles que ce que notre planète peut produire en une année &mdash; et ce, en moins de huit mois. Ce dépassement est dû à la surpêche, à la surexploitation forestière et au rejet dans l’atmosphère de dioxyde de carbone dans des quantités supérieures à la capacité d’absorption des forêts. Nous devons faire reculer cette date (#MoveTheDate). Et nous ne devons pas cesser de la faire reculer avant d'avoir retrouvé un équilibre entre ce que nous consommons et ce que la planète peut produire.",
          "paraC": "Sachant cela, les entreprises doivent considérer leur stratégie et leur performance environnementale sous trois angles :"
        },
        "table2": {
          "row1": {
            "cell1": {
              "title": "Climat"
            },
            "cell2": {
              "p1": "Votre modèle économique et votre chaîne logistique mondiale sont-ils compatibles avec l’objectif <strong>+2 °C</strong>?",
              "p2": "Chez Schneider Electric, nous quantifions activement la devise « Davantage de Schneider Electric pour un meilleur climat » en évaluant notre propre empreinte CO₂ et en calculant le volume d’émissions de CO₂ évitées par nos clients grâce à l’utilisation de nos technologies."
            }
          },
          "row2": {
            "cell1": {
              "title": "Planète"
            },
            "cell2": {
              "p1": "Votre consommation de matières premières et votre stratégie de récupération circulaire est-elle compatible avec l’idée d'une seule planète ?",
              "p2": "Chez Schneider Electric, nous nous sommes associés au <em>Global Footprint Network</em> pour évaluer en permanence si, où et quand nous contribuons chaque année à faire reculer la date (#MoveTheDate), et ce que nous pouvons faire pour intensifier nos efforts."
            }
          },
          "row3": {
            "cell1": {
              "title": "Vie"
            },
            "cell2": {
              "p1": "Est-ce que les pratiques de votre chaîne logistique et vos propositions de valeur sont compatibles avec la <strong>préservation de la vie</strong> ?",
              "p2": "Chez Schneider Electric, nous surveillons  l'utilisation de substances et produits chimiques dangereux et nous cherchons à les réduire ou à les supprimer totalement, que ce soit dans nos usines, dans nos produits ou dans nos emballages. Nos déchets dangereux comme nos émissions doivent être strictement gérés."
            }
          }
        },
        "references": {
          "r1": "<sup>1</sup> http://www.independent.co.uk/environment/solar-and-wind-power-cheaper-than-fossil-fuels-for-the-first-time-a7509251.html",
          "r2": "<sup>2</sup> According to the Global Footprint Network"
        }
      }
    }
  }
}
</i18n>

<template>
  <div id="">
    <Hero :loResSrc="require('../../assets/img/introduction/introduction-environmental-strategy-hero-lowres.jpg')" :hiResSrc="require('../../assets/img/introduction/introduction-environmental-strategy-hero.jpg')" chapter="01" :section="$t('introduction.section')" :title="$t('introduction.title')"/>

    <section class="m-b-120 m-t-120">
      <YouTubeVideo videoId="Q9_22HZirqE" :title="$t('introduction.strategy.vid.title')" :description="$t('introduction.strategy.vid.description')" :preview="require('@/assets/img/thumbnails/SSDHighlightVideo_XavierHouot_Still.jpg')"/>
    </section>

    <section class="l-bg-white l-section-pad">
      <TxtMedia class="m-b-120">
        <h2 class="m-b-35" slot="main-content" v-html="$t('introduction.strategy.innovationEngine.title')"/>
        <p slot="main-content" v-html="$t('introduction.strategy.innovationEngine.para')" />
        <p slot="main-content" v-html="$t('introduction.strategy.innovationEngine.paraB')" />
        <img slot="sidebar" src="../../assets/img/introduction/introduction-environmental-strategy-engine-innovation.jpg" />
      </TxtMedia>

      <ImageFull class="m-b-120 m-t-120">
        <img src="@/assets/img/introduction/introduction-environmental-strategy-sustainability.jpg" />
      </ImageFull>
      <Txt>
        <h2 id="environmentalresponsibility" class="m-b-35" v-html="$t('introduction.strategy.businessGrowth.title')" />
        <p v-html="$t('introduction.strategy.businessGrowth.para')" />
        <ul>
          <li v-html="$t('introduction.strategy.businessGrowth.itemA')" />
          <li v-html="$t('introduction.strategy.businessGrowth.itemB')" />
          <li v-html="$t('introduction.strategy.businessGrowth.itemC')" />
          <li v-html="$t('introduction.strategy.businessGrowth.itemD')" />
        </ul>
        <p v-html="$t('introduction.strategy.businessGrowth.paraB')" />
      </Txt>
    </section>

    <section class="m-b-120 m-t-120">
      <Barometer>
        <h2 class="t-grey" slot="copy" v-html="$t('introduction.strategy.barometer.title')"/>
        <CTA slot="copy" href="/static/PSBarometer-Q42017_results.pdf" :buttonText="$t('introduction.strategy.barometer.cta')" target="_blank"/>
        <BarometerIndicator class="col-xs-18 col-sm-9" :color="$root.sassVars.cGreenDark" :title="$t('introduction.strategy.barometer.one.title')" :stat="$t('introduction.strategy.barometer.one.stat')" :body="$t('introduction.strategy.barometer.one.body')"/>
        <BarometerIndicator class="col-xs-18 col-sm-9" :color="$root.sassVars.cGreenLight" :title="$t('introduction.strategy.barometer.two.title')" :stat="$t('introduction.strategy.barometer.two.stat')" :body="$t('introduction.strategy.barometer.two.body')"/>
      </Barometer>
    </section>

    <section class="l-bg-white l-section-pad m-b-120 m-t-120">
      <TxtMedia class="m-b-32">
        <h2 class="m-b-35" slot="main-content" v-html="$t('introduction.strategy.strategyPillars.title')" />
        <p slot="main-content" v-html="$t('introduction.strategy.strategyPillars.para')" />
        <img slot="sidebar" src="../../assets/img/introduction/introduction-environmental-strategy-our-six-pillars.jpg" alt="">
      </TxtMedia>
      <Table class="m-b-45" columns="2">
        <TableRow>
          <TableCell class="t-white" bgc="#3dcd58">
            <h3 class="t-white" v-html="$t('introduction.strategy.table1.row1.cell1.title')"></h3>
          </TableCell>
          <TableCell >
            <p class="t-chartbody" v-html="$t('introduction.strategy.table1.row1.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <div class="t-h3">
              <span v-html="$t('introduction.strategy.table1.row2.cell1.title')"></span>
              <span v-if="this.$i18n.locale === 'en'"><router-link v-html="$t('introduction.strategy.table1.row2.cell1.link')" :to="{name: 'SustainableSolutions_SupplyChain'}"></router-link></span>
            </div>
          </TableCell>
          <TableCell :bgimg="require('../../assets/img/introduction/GettyImages-692753467.jpg')">
            <p class="t-chartbody t-white" v-html="$t('introduction.strategy.table1.row2.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('../../assets/img/introduction/GettyImages-688024767.jpg')">
            <h3 class="t-white" v-html="$t('introduction.strategy.table1.row3.cell1.title')"></h3>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('introduction.strategy.table1.row3.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 v-html="$t('introduction.strategy.table1.row4.cell1.title')"></h3>
          </TableCell>
          <TableCell class="t-white" bgc="#3dcd58">
            <p class="t-chartbody" v-html="$t('introduction.strategy.table1.row4.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('../../assets/img/introduction/GettyImages-623104412.jpg')">
            <h3 class="t-white" v-html="$t('introduction.strategy.table1.row5.cell1.title')"></h3>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('introduction.strategy.table1.row5.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 v-html="$t('introduction.strategy.table1.row6.cell1.title')"></h3>
          </TableCell>
          <TableCell class="t-white" bgc="#3dcd58">
            <div class="t-chartbody">
              <span v-html="$t('introduction.strategy.table1.row6.cell2.p1')"></span>
              <span class="t-white"><router-link v-html="$t('introduction.strategy.table1.row6.cell2.link')" :to="{name: 'SustainableSolutions_SupplyChain'}"></router-link></span>
              <span v-html="$t('introduction.strategy.table1.row6.cell2.p2')"></span>
            </div>
          </TableCell>
        </TableRow>
      </Table>
      <Statement class="m-t-120">
        <h3 v-html="$t('introduction.strategy.statement1')"></h3>
      </Statement>
    </section>

    <section class="m-b-120 m-t-120">
      <TxtMedia>
        <h3 id="sciencebasedtargets" class="m-b-35" slot="main-content" v-html="$t('introduction.strategy.targetsInitiative.title')"/>
        <p slot="main-content" v-html="$t('introduction.strategy.targetsInitiative.paraA')"/>
        <p slot="main-content">
          <span v-html="$t('introduction.strategy.targetsInitiative.paraB1')"></span>
          <router-link class="t-green-light" v-html="$t('introduction.strategy.targetsInitiative.paraBLink')" :to="{name: 'SustainableSolutions_EnergyServices'}"></router-link>
          <span v-html="$t('introduction.strategy.targetsInitiative.paraB2')"></span>
        </p>
        <ul slot="main-content">
          <li slot="main-content" v-html="$t('introduction.strategy.targetsInitiative.itemA')"/>
          <li slot="main-content" v-html="$t('introduction.strategy.targetsInitiative.itemB')"/>
        </ul>
        <p slot="main-content" v-html="$t('introduction.strategy.targetsInitiative.paraC')"/>
        <img slot="sidebar" src="../../assets/img/introduction/introduction-environmental-strategy-science-based-targets.jpg" alt="">
      </TxtMedia>
    </section>

    <section class="l-bg-white l-section-pad m-b-120 m-t-120">
      <TxtMedia class="m-b-0">
        <h3 id="movethedate" class="m-b-35" slot="main-content" v-html="$t('introduction.strategy.moveTheDate.title')"/>
        <p slot="main-content" v-html="$t('introduction.strategy.moveTheDate.para')"/>
        <p slot="main-content" v-html="$t('introduction.strategy.moveTheDate.paraB')"/>
        <p slot="main-content" v-html="$t('introduction.strategy.moveTheDate.paraC')"/>
        <img slot="sidebar" src="../../assets/img/introduction/introduction-environmental-strategy-move-the-date.jpg" alt="">
      </TxtMedia>
      <Table class="m-b-45" columns="2">
        <TableRow>
          <TableCell class="minheight" :bgimg="require('../../assets/img/introduction/GettyImages-499134783.jpg')">
            <h4 class="t-white" v-html="$t('introduction.strategy.table2.row1.cell1.title')"></h4>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('introduction.strategy.table2.row1.cell2.p1')"></p>
            <p class="t-chartbody" v-html="$t('introduction.strategy.table2.row1.cell2.p2')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h4 class="t-green-light" v-html="$t('introduction.strategy.table2.row2.cell1.title')"></h4>
          </TableCell>
          <TableCell class="t-white" bgc="#3dcd58">
            <p class="t-chartbody" v-html="$t('introduction.strategy.table2.row2.cell2.p1')"></p>
            <p class="t-chartbody" v-html="$t('introduction.strategy.table2.row2.cell2.p2')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('../../assets/img/introduction/GettyImages-514412831.jpg')">
            <h4 class=" t-white" v-html="$t('introduction.strategy.table2.row3.cell1.title')"></h4>
          </TableCell>
          <TableCell class="minheight">
            <p class="t-chartbody" v-html="$t('introduction.strategy.table2.row3.cell2.p1')"></p>
            <p class="t-chartbody" v-html="$t('introduction.strategy.table2.row3.cell2.p2')"></p>
          </TableCell>
        </TableRow>
      </Table>
      <References>
        <Reference v-html="$t('introduction.strategy.references.r1')" />
        <Reference v-html="$t('introduction.strategy.references.r2')" />
      </References>
    </section>

   <div class="l-content">
      <FooterNav
        nextRoute="SustainableSolutions_Overview"
        prevRoute="Introduction_WorkIndex" />
    </div>

  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import YouTubeVideo from '@/components/YouTubeVideo'
import Txt from '@/components/Txt'
import TxtMedia from '@/components/TxtMedia'
import Barometer from '@/components/Barometer'
import BarometerIndicator from '@/components/BarometerIndicator'
import CTA from '@/components/CTA'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'
import Statement from '@/components/Statement'
import ImageFull from '@/components/ImageFull'
import References from '@/components/References'
import Reference from '@/components/Reference'

export default {
  name: 'Introduction_EnvironmentalStrategy',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    YouTubeVideo,
    Txt,
    TxtMedia,
    Barometer,
    BarometerIndicator,
    CTA,
    Table,
    TableRow,
    TableCell,
    Statement,
    References,
    Reference,
    ImageFull
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/introduction/environmental-strategy.vue